import { TabContext, TabPanel } from '@mui/lab';
import { Box, SvgIconProps, Tab, Tabs as MuiTabs, Skeleton } from '@mui/material';
import { ElementType, ReactNode, SyntheticEvent, useState } from 'react';
import { StyledTabsContainer } from './styles';
import { SafeMarkdown } from '@/components/atoms/markdown/safe';

interface TabsProps {
  initialValue?: string | number;
  tabs: {
    note?: number;
    icon?: ElementType<SvgIconProps>;
    label: string;
    value: string | number;
    tab: ReactNode;
    children?: ReactNode;
  }[];
  children?: ReactNode;
  contentStyle?: {
    paddingTop?: number;
    paddingBottom?: number;
    paddingLeft?: number;
    paddingRight?: number;
  };
  showBackground?: boolean;
  loading?: boolean;
  onGetTabValue?: (value: string | number) => void;
  maxWidth?: string;
  isMobile?: boolean;
  id?: string;
}

export const Tabs = ({
  tabs,
  children,
  contentStyle,
  showBackground,
  initialValue,
  loading,
  onGetTabValue,
  maxWidth,
  isMobile,
  id,
}: TabsProps) => {
  const [value, setValue] = useState(initialValue || tabs[0].value);

  const handleChange = (_: SyntheticEvent, newValue: string) => {
    setValue(newValue);
    onGetTabValue?.(newValue);
  };

  return (
    <Box sx={{ width: '100%', height: '100%' }}>
      <TabContext value={value.toString()}>
        <Box
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            backgroundColor: showBackground ? 'background.paper' : 'transparent',
          }}
        >
          <StyledTabsContainer maxwidth={maxWidth}>
            {loading ? (
              <Skeleton animation="wave" height={48} width="100%" sx={{ transform: 'none' }} />
            ) : (
              <MuiTabs onChange={handleChange} value={value} id={id}>
                {tabs.map((tab) => (
                  <Tab
                    key={tab.value}
                    label={<SafeMarkdown text={tab.label} />}
                    value={tab.value}
                    sx={(theme) => ({
                      textTransform: 'none',
                      padding: theme.spacing(0),
                      width: isMobile ? '50%' : 'auto',
                      color:
                        value === tab.value
                          ? theme.palette.primary.main
                          : theme.palette.text.secondary,
                      minHeight: '48px',
                      '& + button': {
                        padding: theme.spacing(0, 2),
                      },
                      [theme.breakpoints.down('md')]: {
                        padding: theme.spacing(0, 2),
                      },
                    })}
                    icon={tab.icon ? <tab.icon /> : undefined}
                    iconPosition="start"
                  />
                ))}
              </MuiTabs>
            )}
          </StyledTabsContainer>
        </Box>

        {children ??
          tabs.map((tab) => {
            if (tab.children) {
              return (
                <TabPanel
                  key={tab.value}
                  value={tab.value.toString()}
                  sx={(theme) => ({
                    padding: theme.spacing(
                      contentStyle?.paddingTop || 0,
                      contentStyle?.paddingRight || 0,
                      contentStyle?.paddingBottom || 0,
                      contentStyle?.paddingLeft || 0
                    ),
                  })}
                >
                  {tab.children}
                </TabPanel>
              );
            }
          })}

        {tabs.map((item) => (
          <TabPanel
            key={item.value}
            value={item.value.toString()}
            sx={(theme) => ({
              padding: theme.spacing(
                contentStyle?.paddingTop || 0,
                contentStyle?.paddingRight || 0,
                contentStyle?.paddingBottom || 0,
                contentStyle?.paddingLeft || 0
              ),
              display: item.value === value ? 'flex' : 'none',
              flexDirection: 'column',
              gap: theme.spacing(3),
              width: '100%',
              height: 'calc(100% - 49px)',
            })}
          >
            {item.tab}
          </TabPanel>
        ))}
      </TabContext>
    </Box>
  );
};
